import { useMemo } from 'react';
import { useBudget } from '../store/BudgetContext';
import { theme } from '../theme';
import { Icon, categoryIconName } from './Icon';
import { fmtK } from '../utils/format';
import { hashStr } from '../utils/coins';
import { BOSS_REWARD, monthKeyOf, pickBoss } from '../utils/game';

/**
 * Босс месяца — Ink-block «афиша противника» (Антверпен 2.0 §1):
 * инверсия цветов, зазубренный HP со щербинами от ударов (трат в категории).
 */
export function BossCard() {
  const { viewData } = useBudget();

  const view = useMemo(() => {
    if (!viewData) return null;
    const boss = pickBoss(viewData.operations, viewData.categories, viewData.settings);
    if (!boss) return null;
    const monthKey = monthKeyOf(new Date());
    const hits = viewData.operations.filter(
      (op) => op.category === boss.cat && op.date.startsWith(monthKey),
    ).length;
    return { boss, hits };
  }, [viewData]);

  if (!view || !viewData) return null;
  const { boss, hits } = view;

  const hpPct = Math.max(0, Math.min(1, boss.hpLeft / boss.hp));
  const lastDay = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();

  // Щербины: по числу ударов (до 6 видимых), глубина детерминирована категорией
  const notches = Math.min(hits, 6);
  const pts: string[] = ['0% 0%'];
  for (let i = 1; i <= notches; i++) {
    const pos = Math.round(10 + (78 * i) / (notches + 1));
    const depth = 30 + (hashStr(`${boss.cat}:${i}`) % 46);
    pts.push(`${pos - 2}% 0%`, `${pos}% ${depth}%`, `${pos + 2}% 0%`);
  }
  pts.push('100% 0%', '96% 28%', '100% 52%', '95% 76%', '100% 100%', '0% 100%');
  const clip = `polygon(${pts.join(',')})`;

  return (
    <div
      style={{
        marginTop: 14,
        background: theme.colors.ink,
        color: theme.colors.paper,
        borderRadius: theme.radius.sm,
        padding: '14px 16px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="caption" style={{ color: theme.colors.paperSub }}>
          противник сезона
        </span>
        {boss.alive && (
          <span
            style={{ fontSize: 12, color: theme.colors.paperSub, fontVariantNumeric: 'tabular-nums' }}
          >
            награда +{BOSS_REWARD} <Icon name="coin" size={12} inline />
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <Icon name={categoryIconName({ name: boss.cat, icon: boss.icon })} size={22} color={theme.colors.paper} />
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontWeight: 420,
            fontVariationSettings: "'opsz' 144, 'WONK' 1",
            fontSize: 23,
          }}
        >
          {boss.cat}
        </span>
      </div>

      <div
        style={{
          position: 'relative',
          height: 16,
          margin: '14px 0 6px',
          background:
            'repeating-linear-gradient(45deg, rgba(236,233,223,.28) 0 1px, transparent 1px 5px)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${hpPct * 100}%`,
            background: theme.colors.paper,
            clipPath: clip,
          }}
        />
      </div>

      {boss.alive ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 13, color: theme.colors.paperSub, whiteSpace: 'nowrap' }}>
              запас прочности{' '}
              <span className="num" style={{ fontSize: 16, color: theme.colors.paper }}>
                {fmtK(boss.hpLeft)}
              </span>{' '}
              из {fmtK(boss.hp)}
            </span>
            <span
              style={{
                fontSize: 12,
                color: theme.colors.paperSub,
                fontStyle: 'italic',
                flexShrink: 1,
                minWidth: 0,
                textAlign: 'right',
              }}
            >
              {hits} {hits === 1 ? 'удар' : hits < 5 ? 'удара' : 'ударов'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: theme.colors.paperSub, fontStyle: 'italic', marginTop: 8 }}>
            щербина — каждый удар по запасу; держись до {lastDay}-го
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: theme.colors.paperSub, fontStyle: 'italic', marginTop: 4 }}>
          противник прорвался ({fmtK(boss.spent)} при плане {fmtK(boss.hp)}) — реванш с 1-го числа
        </div>
      )}
    </div>
  );
}
